import React from 'react';
import { Pencil, Save, X, Flame, Drumstick, Wheat, Droplet, Trash2 } from 'lucide-react';

const MacroResultCard = ({ calories, carbs, protein, fat, description, time, serving_size, _source, name, onEdit, onDelete, isExercise }) => {
  const [editMode, setEditMode] = React.useState(false);
  const [editValues, setEditValues] = React.useState({ name, calories, carbs, protein, fat, serving_size, description });

  React.useEffect(() => {
    setEditValues({ name, calories, carbs, protein, fat, serving_size, description });
  }, [name, calories, carbs, protein, fat, serving_size, description]);

  const handleChange = (e) => {
    setEditValues({ ...editValues, [e.target.name]: e.target.value });
  };

  const handleSave = () => {
    setEditMode(false);
    if (onEdit) onEdit({ ...editValues });
  };
  const handleCancel = () => {
    setEditMode(false);
    setEditValues({ name, calories, carbs, protein, fat, serving_size, description });
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this entry?')) {
      onDelete();
    }
  };

  return (
    <div className="bg-white/90 backdrop-blur-sm rounded-2xl border border-emerald-100/50 hover:border-emerald-200 p-4 mb-3 transition-all duration-200 hover:shadow-lg group">
      {/* Header with macro values */}
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          {editMode ? (
            <input 
              type="text" 
              name="name" 
              value={editValues.name} 
              onChange={handleChange} 
              className="text-lg font-semibold text-gray-800 bg-gray-50 border border-gray-200 rounded-lg px-2 py-1 w-full focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="Food name" 
            />
          ) : (
            <h3 className="text-lg font-semibold text-gray-800 mb-1">{name}</h3>
          )}
        </div>
        <div className="flex gap-2 ml-3 relative z-10">
          {editMode ? (
            <>              <button 
                onClick={handleSave}
                className="w-8 h-8 flex items-center justify-center bg-emerald-100 hover:bg-emerald-200 text-emerald-700 rounded-lg transition-all duration-200 hover:scale-105 cursor-pointer"
                title="Save changes"
                style={{ pointerEvents: 'auto' }}
              >
                <Save size={14} />
              </button>
              <button 
                onClick={handleCancel}
                className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition-all duration-200 hover:scale-105 cursor-pointer"
                title="Cancel editing"
                style={{ pointerEvents: 'auto' }}
              >
                <X size={14} />
              </button>
            </>          ) : (
            <>              <button 
                onClick={() => setEditMode(true)}
                className="w-8 h-8 flex items-center justify-center bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg transition-all duration-200 hover:scale-105 cursor-pointer"
                title="Edit macro values"
                style={{ pointerEvents: 'auto' }}
              >
                <Pencil size={14} />
              </button>
              <button 
                onClick={handleDelete}
                className="w-8 h-8 flex items-center justify-center bg-red-100 hover:bg-red-200 text-red-600 rounded-lg transition-all duration-200 hover:scale-105 cursor-pointer"
                title="Delete entry"
                style={{ pointerEvents: 'auto' }}
              >
                <Trash2 size={14} />
              </button>
            </>
          )}
        </div>
      </div>      {/* Macro values grid */}
      <div className={`grid gap-3 mb-3 ${isExercise ? 'grid-cols-1' : 'grid-cols-2 md:grid-cols-4'}`}>
        <div className="flex items-center gap-2 bg-orange-50 p-3 rounded-xl">
          <div className="w-6 h-6 bg-orange-100 rounded-lg flex items-center justify-center">
            <Flame className="text-orange-600" size={12} />
          </div>
          <div>
            <p className="text-xs text-gray-600">{isExercise ? 'Calories Burned' : 'Calories'}</p>
            {editMode ? (
              <input 
                type="number" 
                name="calories" 
                value={editValues.calories} 
                onChange={handleChange} 
                className="font-semibold text-gray-800 bg-transparent border-none p-0 w-16 focus:outline-none"
              />
            ) : (
              <p className="font-semibold text-gray-800 text-sm">{calories}</p>
            )}
          </div>
        </div>
        
        {!isExercise && (
          <>
            <div className="flex items-center gap-2 bg-purple-50 p-3 rounded-xl">
              <div className="w-6 h-6 bg-purple-100 rounded-lg flex items-center justify-center">
                <Drumstick className="text-purple-600" size={12} />
              </div>
              <div>
                <p className="text-xs text-gray-600">Protein</p>
                {editMode ? (
                  <div className="flex items-center">
                    <input 
                      type="number" 
                      name="protein" 
                      value={editValues.protein} 
                      onChange={handleChange} 
                      className="font-semibold text-gray-800 bg-transparent border-none p-0 w-12 focus:outline-none"
                    />
                    <span className="text-sm text-gray-600">g</span>
                  </div>
                ) : (
                  <p className="font-semibold text-gray-800 text-sm">{protein}g</p>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-2 bg-yellow-50 p-3 rounded-xl">
              <div className="w-6 h-6 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Wheat className="text-yellow-600" size={12} />
              </div>
              <div>
                <p className="text-xs text-gray-600">Carbs</p>
                {editMode ? (
                  <div className="flex items-center">
                    <input 
                      type="number" 
                      name="carbs" 
                      value={editValues.carbs} 
                      onChange={handleChange} 
                      className="font-semibold text-gray-800 bg-transparent border-none p-0 w-12 focus:outline-none"
                    />
                    <span className="text-sm text-gray-600">g</span>
                  </div>
                ) : (
                  <p className="font-semibold text-gray-800 text-sm">{carbs}g</p>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-2 bg-pink-50 p-3 rounded-xl">
              <div className="w-6 h-6 bg-pink-100 rounded-lg flex items-center justify-center">
                <Droplet className="text-pink-600" size={12} />
              </div>
              <div>
                <p className="text-xs text-gray-600">Fat</p>
                {editMode ? (
                  <div className="flex items-center">
                    <input 
                      type="number" 
                      name="fat" 
                      value={editValues.fat} 
                      onChange={handleChange} 
                      className="font-semibold text-gray-800 bg-transparent border-none p-0 w-12 focus:outline-none"
                    />
                    <span className="text-sm text-gray-600">g</span>
                  </div>
                ) : (
                  <p className="font-semibold text-gray-800 text-sm">{fat}g</p>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Serving size */}
      {(editMode || serving_size) && (
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-600 mb-1">Serving Size</label>
          {editMode ? (
            <input 
              type="text" 
              name="serving_size" 
              value={editValues.serving_size || ''} 
              onChange={handleChange} 
              className="w-full text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="e.g., 100g, 1 cup" 
            />
          ) : (
            <p className="text-sm text-gray-700">{serving_size}</p>
          )}
        </div>
      )}

      {/* Description */}
      {(editMode || description) && (
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
          {editMode ? (
            <textarea 
              name="description" 
              value={editValues.description || ''} 
              onChange={handleChange} 
              className="w-full text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="Additional details about the food"
              rows="2"
            />
          ) : (
            <p className="text-sm text-gray-700">{description}</p>
          )}
        </div>
      )}

      {/* Footer with source and time */}
      <div className="flex justify-between items-center text-xs text-gray-500 pt-2 border-t border-gray-100">
        <div className="flex items-center gap-2">
          {_source && (
            <span className="bg-gray-100 px-2 py-1 rounded-md">
              {_source === 'openfoodfacts' ? 'Open Food Facts' : 'AI Estimate'}
            </span>
          )}
        </div>
        {time && (
          <span className="flex items-center gap-1">
            <span>🕒</span>
            {time}
          </span>
        )}
      </div>
    </div>
  );
};

export default MacroResultCard;
